import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, Download, Mail, MapPin, Phone, Quote, User } from "lucide-react";
import { useContent } from "../i18n/content";
import { MaskedLine, Reveal, Counter } from "../components/motion/Reveal";

const Block = ({ overline, title, children, dark = false, testid }) => (
    <section
        data-testid={testid}
        className={`py-20 lg:py-28 ${dark ? "bg-[#24251f]" : ""}`}
    >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <Reveal>
                <div className="flex items-center gap-4 mb-4">
                    <span className="h-px w-10 bg-[#f6c422]/60" aria-hidden="true" />
                    <span className="overline-text">{overline}</span>
                </div>
                <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white mb-12">
                    {title}
                </h2>
            </Reveal>
            {children}
        </div>
    </section>
);

const ManagementGrid = ({ people, testid }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {people.map((p, i) => (
            <Reveal key={p.name + p.role} delay={0.06 * i}>
                <div
                    data-testid={`${testid}-${i}`}
                    className="group border border-[#4a4c42] bg-[#292a24] p-6 flex items-center gap-5 hover:border-[#f6c422]/60 transition-colors duration-500"
                >
                    <div className="w-14 h-14 rounded-full border border-[#4a4c42] bg-[#32332c] flex items-center justify-center shrink-0 group-hover:border-[#f6c422]/60 transition-colors duration-500">
                        <User className="w-6 h-6 text-[#a2a59d] group-hover:text-[#f6c422] transition-colors duration-500" strokeWidth={1.2} />
                    </div>
                    <div>
                        <h3 className="font-display font-bold text-white leading-snug">{p.name}</h3>
                        <p className="overline-text !text-[9px] mt-1.5">{p.role}</p>
                    </div>
                </div>
            </Reveal>
        ))}
    </div>
);

export default function CompanyPage() {
    const { slug } = useParams();
    const { ui, getCompany } = useContent();
    const cp = ui.company;
    const company = getCompany(slug);

    useEffect(() => {
        window.__lenis ? window.__lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0);
    }, [slug]);

    if (!company) return <Navigate to="/" replace />;

    const c = company;

    return (
        <main data-testid={`company-page-${c.slug}`}>
            {/* Hero */}
            <section className="relative min-h-[82vh] flex flex-col justify-end overflow-hidden">
                <div className="absolute inset-0 kenburns">
                    <img src={c.image} alt={`${c.fullName}`} className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-[#292a24]/65" />
                <div className="absolute inset-0 hero-grid" />
                <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#292a24] to-transparent" />

                <div className="relative z-10 max-w-[1440px] mx-auto w-full px-6 lg:px-12 pb-16 pt-40">
                    <Reveal>
                        <Link
                            to="/"
                            data-testid="back-to-sadja-core-button"
                            className="inline-flex items-center gap-2 font-display text-[11px] font-semibold tracking-[0.2em] uppercase text-[#a2a59d] hover:text-[#f6c422] transition-colors duration-300 mb-10"
                        >
                            <ArrowLeft className="w-4 h-4" /> {cp.backTo}
                        </Link>
                    </Reveal>
                    {c.logo && (
                        <Reveal delay={0.15}>
                            <div className="w-20 h-20 bg-white rounded-sm p-2 flex items-center justify-center mb-8 shadow-xl">
                                <img src={c.logo} alt={`${c.fullName} logo`} className="max-w-full max-h-full object-contain" />
                            </div>
                        </Reveal>
                    )}
                    <p className="overline-text mb-6 flex items-center gap-4">
                        <span className="h-px w-12 bg-[#f6c422]" aria-hidden="true" />
                        {c.category}
                    </p>
                    <h1 className="font-display font-extrabold uppercase leading-[0.98] tracking-tight text-[10vw] sm:text-5xl lg:text-7xl text-white max-w-5xl">
                        <MaskedLine delay={0.25}>{c.heroTitle}</MaskedLine>
                    </h1>
                    <Reveal delay={0.5}>
                        <p className="font-display text-sm sm:text-base font-semibold tracking-[0.22em] uppercase text-[#f6c422] mt-6">
                            {c.heroSubtitle}
                        </p>
                        <p className="text-[#d8d9d2] max-w-2xl mt-4 leading-relaxed">{c.description}</p>
                    </Reveal>
                    {c.highlight && (
                        <Reveal delay={0.65}>
                            <div className="mt-10 inline-flex items-baseline gap-4 border border-[#f6c422]/40 bg-[#292a24]/60 backdrop-blur-md px-7 py-5">
                                <Counter
                                    value="20"
                                    suffix="+"
                                    className="font-display text-5xl font-extrabold text-[#f6c422]"
                                />
                                <span className="font-display text-xs uppercase tracking-[0.22em] text-white/80">
                                    {c.highlight.label}
                                </span>
                            </div>
                        </Reveal>
                    )}
                </div>
            </section>

            {/* About */}
            <Block overline={c.fullName} title={c.slug === "sadja-group" ? cp.companyProfile : `${cp.about} ${c.slug === "digon" ? "Digon" : "Orbito"}`} testid="company-about">
                <div className="max-w-3xl space-y-6">
                    {c.about.map((p, i) => (
                        <Reveal key={i} delay={0.08 * i}>
                            <p className="text-[#d8d9d2] leading-relaxed text-base lg:text-lg">{p}</p>
                        </Reveal>
                    ))}
                </div>
            </Block>

            {/* Vision & Mission */}
            {c.vision && (
                <Block overline={cp.direction} title={cp.visionMission} dark testid="company-vision-mission">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                        <Reveal>
                            <div className="border border-[#4a4c42] bg-[#292a24] p-8 h-full">
                                <Quote className="w-6 h-6 text-[#f6c422] mb-5" strokeWidth={1.5} />
                                <p className="overline-text !text-[10px] mb-3">{cp.vision}</p>
                                <p className="text-white text-lg leading-relaxed">{c.vision}</p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.12}>
                            <div className="border border-[#4a4c42] bg-[#292a24] p-8 h-full">
                                <p className="overline-text !text-[10px] mb-5">{cp.mission}</p>
                                <ul className="space-y-4">
                                    {c.mission.map((m) => (
                                        <li key={m} className="flex items-start gap-3 text-sm text-[#d8d9d2] leading-relaxed">
                                            <Check className="w-4 h-4 text-[#f6c422] shrink-0 mt-0.5" strokeWidth={2.5} />
                                            {m}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                    </div>
                </Block>
            )}

            {/* Strengths / Specializations */}
            {(c.strengths || c.specializations) && (
                <Block
                    overline={c.slug === "sadja-group" ? cp.specializations : cp.strengthsOverline}
                    title={c.slug === "sadja-group" ? cp.specializations : cp.strengthsTitle}
                    testid="company-strengths"
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#4a4c42] border border-[#4a4c42]">
                        {(c.strengths || c.specializations).map((s, i) => (
                            <Reveal key={s.title} delay={0.06 * i} className="h-full">
                                <div className="h-full bg-[#292a24] p-7 hover:bg-[#32332c] transition-colors duration-500 group">
                                    <span className="font-display text-xs text-[#f6c422] tracking-[0.24em]">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <h3 className="font-display text-base font-bold uppercase tracking-wide text-white mt-3 group-hover:text-[#f6c422] transition-colors duration-300">
                                        {s.title}
                                    </h3>
                                    <p className="text-sm text-[#a2a59d] mt-2 leading-relaxed">{s.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </Block>
            )}

            {/* Services */}
            {c.services && (
                <Block overline={cp.capabilities} title={cp.services} dark testid="company-services">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#4a4c42] border border-[#4a4c42]">
                        {c.services.map((s, i) => (
                            <Reveal key={s} delay={0.05 * i}>
                                <div
                                    data-testid={`company-service-${i}`}
                                    className="flex items-center gap-6 bg-[#24251f] px-7 py-6 hover:bg-[#32332c] transition-colors duration-500 group"
                                >
                                    <span className="font-display text-2xl font-extrabold text-stroke-yellow shrink-0">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="font-display text-sm font-semibold uppercase tracking-wider text-white group-hover:text-[#f6c422] transition-colors duration-300">
                                        {s}
                                    </span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </Block>
            )}

            {/* EPCC (Orbito) */}
            {c.epcc && (
                <Block overline={cp.epccOverline} title={cp.epccTitle} testid="company-epcc">
                    <p className="text-[#a2a59d] max-w-2xl -mt-6 mb-12 leading-relaxed">
                        {cp.epccDesc}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {c.epcc.map((e, i) => (
                            <Reveal key={e.title} delay={0.08 * i}>
                                <div className="h-full border border-[#4a4c42] bg-[#292a24] p-8 hover:border-[#f6c422]/60 transition-colors duration-500">
                                    <span className="font-display text-3xl font-extrabold text-stroke-yellow">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <h3 className="font-display text-xl font-extrabold uppercase tracking-wide text-white mt-3">
                                        {e.title}
                                    </h3>
                                    <p className="text-sm text-[#a2a59d] mt-3 leading-relaxed">{e.desc}</p>
                                    <ul className="mt-5 space-y-2">
                                        {e.items.map((item) => (
                                            <li key={item} className="flex items-center gap-3 text-sm text-white/80">
                                                <span className="w-1.5 h-1.5 rotate-45 bg-[#f6c422]/80 shrink-0" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </Block>
            )}

            {/* Project Focus (Orbito) */}
            {c.projectFocus && (
                <Block overline={cp.projectFocusOverline} title={cp.projectFocusTitle} dark testid="company-project-focus">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {c.projectFocus.map((p) => (
                            <Reveal key={p.title}>
                                <div className="h-full border-l-2 border-[#f6c422] bg-[#292a24] p-8">
                                    <h3 className="font-display text-lg font-extrabold uppercase tracking-wide text-white">
                                        {p.title}
                                    </h3>
                                    <p className="text-sm text-[#a2a59d] mt-3 leading-relaxed">{p.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </Block>
            )}

            {/* Specialization (Orbito) */}
            {c.specialization && (
                <Block overline={cp.specializationOverline} title={c.specialization.title} testid="company-specialization">
                    <p className="text-[#d8d9d2] max-w-3xl -mt-6 mb-12 leading-relaxed">{c.specialization.desc}</p>
                    <div className="flex flex-wrap gap-3">
                        {c.specialization.points.map((p, i) => (
                            <Reveal key={p} delay={0.05 * i}>
                                <span className="inline-block font-display text-xs font-semibold tracking-[0.14em] uppercase text-[#f6c422] border border-[#f6c422]/40 px-4 py-2.5">
                                    {String(i + 1).padStart(2, "0")} — {p}
                                </span>
                            </Reveal>
                        ))}
                    </div>
                </Block>
            )}

            {/* Why Partner (Orbito) */}
            {c.whyPartner && (
                <Block overline={cp.partnership} title={cp.whyPartner} dark testid="company-why-partner">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#4a4c42] border border-[#4a4c42]">
                        {c.whyPartner.map((w, i) => (
                            <Reveal key={w} delay={0.05 * i}>
                                <div className="bg-[#24251f] p-7 h-full hover:bg-[#32332c] transition-colors duration-500">
                                    <span className="font-display text-xs text-[#f6c422] tracking-[0.24em]">{String(i + 1).padStart(2, "0")}</span>
                                    <h3 className="font-display text-base font-bold uppercase tracking-wide text-white mt-3">{w}</h3>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </Block>
            )}

            {/* Projects / Track Record */}
            {c.projects && (
                <Block overline={c.slug === "orbito" ? cp.expertiseProjects : cp.projectExperience} title={c.slug === "orbito" ? cp.selectedPortfolio : cp.projectExperience} dark={c.slug === "orbito"} testid="company-projects">
                    {c.projectIntro && (
                        <p className="text-[#d8d9d2] max-w-3xl -mt-6 mb-12 leading-relaxed">{c.projectIntro}</p>
                    )}
                    <div className="border-t border-[#4a4c42]">
                        {c.projects.map((p, i) => (
                            <Reveal key={`${p.name}-${i}`} delay={0.03 * i}>
                                <div
                                    data-testid={`company-project-${i}`}
                                    className="group grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 items-baseline py-5 border-b border-[#4a4c42] hover:bg-[#32332c]/50 transition-colors duration-300 px-2 sm:px-4"
                                >
                                    <span className="sm:col-span-1 font-display text-xs text-[#a2a59d]/50 tracking-[0.2em]">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <h3 className="sm:col-span-6 font-display text-base font-bold text-white group-hover:text-[#f6c422] transition-colors duration-300 leading-snug">
                                        {p.name}
                                    </h3>
                                    <span className="sm:col-span-3 text-sm text-[#a2a59d]">{p.client}</span>
                                    <span className="sm:col-span-2 text-xs font-display font-semibold tracking-wider uppercase text-[#f6c422]/90">
                                        {p.year || p.scope}
                                    </span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    {c.clientsNote && (
                        <p className="overline-text !text-[10px] mt-8">{c.clientsNote}</p>
                    )}
                </Block>
            )}

            {/* Track Record (Sadja Group) */}
            {c.trackRecord && (
                <Block overline={cp.trackRecordOverline} title={cp.trackRecord} dark testid="company-track-record">
                    <div className="border-t border-[#4a4c42]">
                        {c.trackRecord.map((t, i) => (
                            <Reveal key={t} delay={0.04 * i}>
                                <div className="flex items-start gap-6 py-5 border-b border-[#4a4c42] px-2 sm:px-4 hover:bg-[#32332c]/50 transition-colors duration-300">
                                    <span className="font-display text-xs text-[#a2a59d]/50 tracking-[0.2em] pt-0.5 shrink-0">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <p className="text-sm text-white/85 leading-relaxed">{t}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </Block>
            )}

            {/* Work Areas (Sadja Group) */}
            {c.workAreas && (
                <Block overline={cp.workAreasOverline} title={cp.workAreasTitle} testid="company-work-areas">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {c.workAreas.map((w, i) => (
                            <Reveal key={w.region} delay={0.06 * i}>
                                <div className="h-full border border-[#4a4c42] bg-[#292a24] p-7 hover:border-[#f6c422]/60 transition-colors duration-500">
                                    <h3 className="font-display text-lg font-extrabold uppercase tracking-[0.12em] text-[#f6c422]">
                                        {w.region}
                                    </h3>
                                    <p className="text-sm text-[#a2a59d] mt-3 leading-relaxed">{w.detail}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </Block>
            )}

            {/* Partners */}
            {c.partners && (
                <Block overline="Jejak Mitra" title="Partners" dark testid="company-partners">
                    <div className="flex flex-wrap gap-3">
                        {c.partners.map((p, i) => (
                            <Reveal key={p} delay={0.04 * i}>
                                <span className="inline-block font-display text-xs font-semibold tracking-[0.12em] uppercase text-white/85 border border-[#4a4c42] bg-[#292a24] px-5 py-3 hover:border-[#f6c422]/60 hover:text-[#f6c422] transition-colors duration-300">
                                    {p}
                                </span>
                            </Reveal>
                        ))}
                    </div>
                </Block>
            )}

            {/* Management */}
            {c.management && (
                <Block overline={cp.managementOverline} title={cp.management} testid="company-management">
                    <ManagementGrid people={c.management} testid="company-leader" />
                </Block>
            )}

            {/* Legality */}
            {c.legality && (
                <Block overline={cp.compliance} title={cp.legality} dark testid="company-legality">
                    <p className="text-[#d8d9d2] max-w-3xl leading-relaxed -mt-6">{c.legality}</p>
                </Block>
            )}

            {/* Contact + PDF */}
            <section data-testid="company-contact" className="py-20 lg:py-28 bg-[#24251f] border-t border-[#4a4c42]">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <div>
                        <Reveal>
                            <div className="flex items-center gap-4 mb-4">
                                <span className="h-px w-10 bg-[#f6c422]/60" aria-hidden="true" />
                                <span className="overline-text">{cp.contact}</span>
                            </div>
                            <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white mb-10">
                                {cp.readyTitle}
                            </h2>
                        </Reveal>
                        <div className="space-y-6">
                            <Reveal delay={0.08}>
                                <a
                                    data-testid="company-email-link"
                                    href={`mailto:${c.contact.email}`}
                                    className="flex items-center gap-4 text-sm text-[#d8d9d2] hover:text-[#f6c422] transition-colors duration-300 break-all"
                                >
                                    <Mail className="w-5 h-5 text-[#f6c422] shrink-0" strokeWidth={1.5} />
                                    {c.contact.email}
                                </a>
                            </Reveal>
                            {c.contact.secondaryEmail && (
                                <Reveal delay={0.1}>
                                    <a
                                        data-testid="company-secondary-email-link"
                                        href={`mailto:${c.contact.secondaryEmail}`}
                                        className="flex items-center gap-4 text-sm text-[#d8d9d2] hover:text-[#f6c422] transition-colors duration-300 break-all"
                                    >
                                        <Mail className="w-5 h-5 text-[#f6c422] shrink-0" strokeWidth={1.5} />
                                        {c.contact.secondaryEmail}
                                    </a>
                                </Reveal>
                            )}
                            {c.contact.phones.map((p, i) => (
                                <Reveal key={p} delay={0.12 + i * 0.04}>
                                    <span
                                        data-testid={`company-phone-${i}`}
                                        className="flex items-center gap-4 text-sm text-[#d8d9d2]"
                                    >
                                        <Phone className="w-5 h-5 text-[#f6c422] shrink-0" strokeWidth={1.5} />
                                        {p}
                                    </span>
                                </Reveal>
                            ))}
                            <Reveal delay={0.2}>
                                <span
                                    data-testid="company-address"
                                    className="flex items-start gap-4 text-sm text-[#a2a59d] leading-relaxed"
                                >
                                    <MapPin className="w-5 h-5 text-[#f6c422] shrink-0 mt-0.5" strokeWidth={1.5} />
                                    {c.contact.address}
                                </span>
                            </Reveal>
                        </div>
                    </div>

                    <Reveal delay={0.12}>
                        <div className="h-full border border-[#f6c422]/40 bg-[#292a24] p-10 flex flex-col justify-center">
                            <p className="overline-text mb-4">Company Profile</p>
                            <h3 className="font-display text-2xl font-extrabold uppercase text-white leading-tight">
                                Download the full {c.cardTitle} company profile
                            </h3>
                            <p className="text-sm text-[#a2a59d] mt-3 leading-relaxed">
                                {c.pdfLabel}
                            </p>
                            <a
                                data-testid="company-pdf-download-button"
                                href={encodeURI(c.pdf)}
                                download
                                className="mt-8 inline-flex items-center justify-center gap-3 bg-[#f6c422] text-[#292a24] font-display font-bold text-xs tracking-[0.18em] uppercase px-8 py-4 hover:bg-[#ffe169] transition-colors duration-300"
                            >
                                <Download className="w-4 h-4" />
                                Click here to download company detail
                            </a>
                            <Link
                                to="/"
                                data-testid="company-back-home-link"
                                className="mt-4 inline-flex items-center justify-center gap-2 border border-white/25 text-white font-display font-semibold text-xs tracking-[0.18em] uppercase px-8 py-4 hover:border-[#f6c422] hover:text-[#f6c422] transition-colors duration-300"
                            >
                                {cp.backTo} <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
